import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';

export interface ResourceDetailCrumb {
    label: string;
    to?: string;
}

export interface ResourceDetailProps {
    crumbs: ResourceDetailCrumb[];
    title: string;
    badge?: ReactNode;
    actions?: ReactNode;
    children: ReactNode;
    aside?: ReactNode;
    /** Aviso de cambios locales que todavía no se han confirmado. */
    unsavedMessage?: string | null;
    onDiscard?: () => void;
}

/**
 * Página de detalle reutilizable: migas, estado, acciones y dos columnas
 * que en móvil se apilan.
 */
export function ResourceDetail({
    crumbs,
    title,
    badge,
    actions,
    children,
    aside,
    unsavedMessage,
    onDiscard,
}: ResourceDetailProps) {
    return (
        <div className="space-y-6">
            <nav aria-label="Migas" className="flex flex-wrap items-center gap-1.5 text-sm text-muted-foreground">
                {crumbs.map((crumb, index) => {
                    const last = index === crumbs.length - 1;
                    return (
                        <span key={`${crumb.label}-${index}`} className="inline-flex items-center gap-1.5">
                            {index > 0 ? <span aria-hidden="true">›</span> : null}
                            {crumb.to && !last ? (
                                <Link to={crumb.to} className="hover:text-foreground">
                                    {crumb.label}
                                </Link>
                            ) : (
                                <span className={last ? 'text-foreground' : undefined}>{crumb.label}</span>
                            )}
                        </span>
                    );
                })}
            </nav>

            <header className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                <div className="min-w-0 space-y-2">
                    <h1 className="text-xl font-bold tracking-tight text-foreground sm:text-2xl">{title}</h1>
                    {badge}
                </div>
                {actions ? <div className="flex flex-wrap items-center gap-2">{actions}</div> : null}
            </header>

            {unsavedMessage ? (
                <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-sm text-amber-200">
                    <p>{unsavedMessage}</p>
                    {onDiscard ? (
                        <button type="button" className="underline" onClick={onDiscard}>
                            Descartar cambios
                        </button>
                    ) : null}
                </div>
            ) : null}

            <div className="grid grid-cols-1 items-start gap-6 lg:grid-cols-[minmax(0,1fr)_20rem]">
                <div className="min-w-0 space-y-6">{children}</div>
                {aside ? <aside className="space-y-4">{aside}</aside> : null}
            </div>
        </div>
    );
}
